import { useCallback, useEffect, useState } from "react";
import {
  fetchCanonicalVehicles,
  recalculateCanonicalVehicle,
  runSourceDuplicateDetection,
} from "../../../../services/vehicleDuplicateApi";
import { fetchVehicleSources } from "../../../../services/vehicleSourceApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import RowActionMenu from "../layout/RowActionMenu";
import CanonicalVehicleDetailPanel from "./CanonicalVehicleDetailPanel";

function formatCop(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const CanonicalVehiclesPage = ({ onClose, initialCanonicalId }) => {
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [selectedId, setSelectedId] = useState(initialCanonicalId || null);

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [canonicalStatus, setCanonicalStatus] = useState("");
  const [department, setDepartment] = useState("");
  const [sources, setSources] = useState([]);
  const [runSourceId, setRunSourceId] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchCanonicalVehicles({
        make: make || undefined,
        model: model || undefined,
        year: year ? Number(year) : undefined,
        canonicalStatus: canonicalStatus || undefined,
        department: department || undefined,
        limit: 200,
      });
      setVehicles(data.vehicles || []);
      setSummary(data.summary || null);
    } catch (error) {
      setVehicles([]);
      setErrorMessage(error.message || "Unable to load canonical vehicles.");
    } finally {
      setLoading(false);
    }
  }, [make, model, year, canonicalStatus, department]);

  useEffect(() => {
    fetchVehicleSources({ sort: "source_name", sortDir: "asc" })
      .then((data) => setSources(data.sources || []))
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (initialCanonicalId) setSelectedId(initialCanonicalId);
  }, [initialCanonicalId]);

  const handleRecalculate = async (id) => {
    try {
      await recalculateCanonicalVehicle(id);
      setToast("Summary recalculated.");
      await load();
    } catch (error) {
      setToast(error.message || "Recalculate failed.");
    }
  };

  const handleSourceRun = async () => {
    if (!runSourceId) return;
    try {
      const result = await runSourceDuplicateDetection(runSourceId, { batchSize: 50 });
      setToast(`Source run complete (${result.summary?.listingsProcessed ?? 0} listings).`);
      await load();
    } catch (error) {
      setToast(error.message || "Duplicate detection run failed.");
    }
  };

  if (selectedId) {
    return (
      <CanonicalVehicleDetailPanel
        canonicalId={selectedId}
        onBack={() => setSelectedId(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="admin-dashboard admin-canonical-vehicles-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <p className="admin-dashboard__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dashboard__title">Canonical Vehicles</h1>
          <p className="admin-dashboard__subtitle">
            Grouped physical vehicles across marketplace and dealer listings.
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="admin-dashboard__action-btn" onClick={load}>
            Refresh
          </button>
          {onClose ? (
            <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {summary ? (
        <section className="admin-dashboard__summary-cards">
          <div className="admin-summary-card">
            <span>Total canonical</span>
            <strong>{summary.total_canonical ?? 0}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Active canonical</span>
            <strong>{summary.active_canonical ?? 0}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Multi-source</span>
            <strong>{summary.multi_source_canonical ?? 0}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Pending review</span>
            <strong>{summary.pending_review ?? 0}</strong>
          </div>
        </section>
      ) : null}

      {toast ? <p className="admin-dashboard__toast">{toast}</p> : null}

      <section className="admin-dashboard__filters">
        <label>
          Make
          <input value={make} onChange={(e) => setMake(e.target.value)} />
        </label>
        <label>
          Model
          <input value={model} onChange={(e) => setModel(e.target.value)} />
        </label>
        <label>
          Year
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
        </label>
        <label>
          Status
          <select value={canonicalStatus} onChange={(e) => setCanonicalStatus(e.target.value)}>
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="STALE">Stale</option>
            <option value="LIKELY_SOLD">Likely sold</option>
            <option value="REMOVED">Removed</option>
            <option value="MIXED">Mixed</option>
          </select>
        </label>
        <label>
          Department
          <input value={department} onChange={(e) => setDepartment(e.target.value)} />
        </label>
        <label>
          Run detection (source)
          <select value={runSourceId} onChange={(e) => setRunSourceId(e.target.value)}>
            <option value="">Select source</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.sourceName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="admin-dashboard__action-btn" onClick={handleSourceRun}>
          Run source detection
        </button>
      </section>

      {loading ? (
        <AdminStatePanel message="Loading canonical vehicles…" />
      ) : errorMessage ? (
        <AdminStatePanel message={errorMessage} variant="error" />
      ) : (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Year</th>
                <th>Location</th>
                <th>Lowest price</th>
                <th>Median price</th>
                <th>Active</th>
                <th>Sources</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id}>
                  <td>
                    <strong>
                      {v.makeNormalized} {v.modelNormalized}
                    </strong>
                    {v.trimNormalized ? (
                      <div className="admin-table__meta">{v.trimNormalized}</div>
                    ) : null}
                  </td>
                  <td>{v.year ?? "—"}</td>
                  <td>
                    {v.city ? `${v.city}, ` : ""}
                    {v.department || "—"}
                  </td>
                  <td>{formatCop(v.lowestCurrentPriceCop)}</td>
                  <td>{formatCop(v.medianCurrentPriceCop)}</td>
                  <td>{v.activeListingCount}</td>
                  <td>{v.sourceCount}</td>
                  <td>{v.canonicalStatus}</td>
                  <td>{formatDate(v.lastSeenAt)}</td>
                  <td>
                    <RowActionMenu
                      actions={[
                        { label: "Open detail", onClick: () => setSelectedId(v.id) },
                        { label: "Recalculate", onClick: () => handleRecalculate(v.id) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!vehicles.length ? <p className="admin-table__empty">No canonical vehicles yet.</p> : null}
        </AdminTableContainer>
      )}
    </div>
  );
};

export default CanonicalVehiclesPage;
